import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useState, useRef, useMemo } from "preact/hooks"
import { productImagePlaceholder, getSrcset, groupVariants, findSkuFromColor, getPriceRange } from "../helpers"
import { INITIAL_SWATCHES_SHOWN } from "../config"

export default function Product({ product, activeColor }) {
    const { format } = useCurrencyFormatting();
    const skuByColor = useMemo(() => groupVariants(product, 'color') || null, [])
    const colors = useMemo(() => skuByColor ? Object.keys(skuByColor) : [], []);
    const badge = useMemo(() => product.customFields?.find(field => field.key === "cql-badge_label")?.value, [])
    const modelImage = useMemo(() => product.customFields?.find(field => field.key === "custom-model_image")?.value, [])
    const priceRange = useMemo(() => getPriceRange(product), []);
    const reviewsRef = useRef(null);

    const [ swatchesDisplayed, setSwatchesDisplayed ] = useState(INITIAL_SWATCHES_SHOWN);
    const [ currentColor, setCurrentColor ] = useState(colors?.[0] || null);
    const [ colorClicked, setColorClicked ] = useState(false);

    const images = useMemo(() => {
        const imagesObj = (skuByColor?.[currentColor])
            ?   { main: skuByColor[currentColor]?.imageUrl || null, 
                  alt: skuByColor[currentColor]?.altImage || null }
            :   { main: product.imageUrl || null,
                  alt: product.alternateImageUrls?.[0] || null }

        return imagesObj
    }, [ currentColor ]);

    const prices = useMemo(() => {
        const pricesObj = (skuByColor?.[currentColor])
            ? { price: skuByColor[currentColor].price, 
                listPrice: skuByColor[currentColor].listPrice || null }
            : { price: product.price, 
                listPrice: product.listPrice || null }
        pricesObj.range = priceRange;

        return pricesObj;
    }, [ currentColor ]);

    useEffect(() => {
        if(
            Math.ceil(product.customFields.find(field => field.key === 'reviews-rating_count')?.value) > 0 &&
            reviewsRef.current && 
            reviewsRef.current.children.length === 0 && 
            typeof window.okeWidgetApi?.initWidget === 'function'
        ) {
            window.okeWidgetApi.initWidget(reviewsRef.current);
        }
    }, [])

    useEffect(() => { // updates preselected swatch if color found in user search query or color filter active
        if(!activeColor || !skuByColor) return;
        const matchingSkuColor = findSkuFromColor(activeColor, skuByColor);
        if(matchingSkuColor !== currentColor) {
            setCurrentColor(matchingSkuColor)
        }
    }, [ activeColor, skuByColor ])

    const handleSkuChange = (color) => {
        if(currentColor !== color) {
            setCurrentColor(color);
            if(!colorClicked) {
                setColorClicked(true);
            }
        }
    }
    
    const showAllSwatches = () => {
        setSwatchesDisplayed(colors.length);
    }

    return (
        <SerpElement
            as="article"
            hit={product}
            class="ns-product ns-flex ns-flex-col w-full"
            aria-live="polite"
        >
            <div class="ns-product__media ns-relative ns-w-full ns-overflow-hidden">
                <button 
                    class={`swym-button swym-add-to-wishlist-view-product product_${product.productId} swym-icon swym-heart ns-absolute`}
                    data-swaction="addToWishlist"
                    data-product-url={product.url}
                    data-product-id={product.productId}
                    data-variant-id={currentColor ? skuByColor[currentColor].id : product.skus?.[0]?.id}
                    aria-label="Add to Wishlist"
                    ns-wishlist-btn
                />
                <div class="ns-product__media-wrapper">
                    {badge && 
                        <div class="ns-product__badges ns-absolute ns-flex ns-flex-nowrap">
                            <span class="ns-product__badge">{badge}</span>
                        </div>
                    }
                    {modelImage ? (
                        <img
                            class="ns-product__image ns-main-image ns-w-full ns-h-full ns-absolute"
                            srcset={modelImage ? getSrcset(modelImage, ['493', '600', '713', '823', '900']) : productImagePlaceholder}
                            src={modelImage?.replace('.jpg', '_900x.jpg')?.replace('.png', '_900x.png')?.replace('.webp', '_900x.webp') ?? productImagePlaceholder}
                            sizes="(min-width: 1270px) 644px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                            alt={product.name}
                            width="600"
                            height="600"
                            loading="lazy"
                        />
                    ) : (
                        <img
                            class="ns-product__image ns-main-image ns-w-full ns-h-full ns-absolute"
                            srcset={images.main ? getSrcset(images.main, ['493', '600', '713', '823', '900']) : productImagePlaceholder}
                            src={images.main?.replace('.jpg', '_900x.jpg')?.replace('.png', '_900x.png')?.replace('.webp', '_900x.webp') ?? productImagePlaceholder}
                            sizes="(min-width: 1270px) 644px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                            alt={product.name}
                            width="600"
                            height="600"
                            loading="lazy"
                        />
                    )}

                    {images?.alt && images.alt !== '' && (
                        <img
                            class="ns-product__image ns-alt-image ns-w-full ns-h-full ns-absolute test"
                            srcset={getSrcset(images.alt, ['493', '600', '713', '823', '900'])}
                            src={images.alt.replace('.jpg', '_900x.jpg').replace('.png', '_900x.png').replace('.webp', '_900x.webp')}
                            sizes="(min-width: 1270px) 644px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                            alt={product.name}
                            width="600"
                            height="600"
                            loading="lazy"
                        />
                    )}
                </div>

                {!product.tags1.includes("personalized_product") ? (
                <quick-view product-url={product.url} product-id={product.productId}></quick-view>
                ) : null}
                
                {product.tags1.includes("Letters and Numbers") ? (
                    <letter-variant-selector product-url={product.url}></letter-variant-selector>
                ) : null}
            </div>
            <script>
                {/* console.log('window location', window.location); */}
                {/* if(window.location.pathname == "/location/sale") {
                    let elements = document.getElementsByClassName('priceRangePrice');
                    for (let i = 0; i < elements.length; i++) {
                        elements[i].style.backgroundColor="#c93933";
                    }
                    console.log('on sale page');
                } */}


            </script>
            <div class="ns-product__metadata" data-nosto-element="product" data-test>            
                <a 
                    className="ns-product__link"
                    href={currentColor ? `${product.url}?variant=${skuByColor[currentColor].id}` : product.url} 
                >
                    <p class="ns-product__title">{product.name}</p>
                    <div className="ns-product__price-container ns-flex ns-flex-nowrap ns-items-center">
                        {!colorClicked && prices.range && prices.range?.lowest < prices.range?.highest ? (
                            <>
                            <span class="sr-only visually-hidden">Price range:</span>
                            <span class="ns-product__price priceRangePrice" style={{color: window.location.pathname=='/collections/sale' ? '#c93933': ''}} >
                                {format(prices.range.lowest, 'USD').replace('.00', '')} to {format(prices.range.highest, 'USD').replace('.00', '')}
                            </span>
                            </>
                        ) : (
                            <>
                            {prices.listPrice && prices.listPrice > prices.price && (
                                <>
                                <span class="sr-only visually-hidden">Original Price:</span>
                                <span class="ns-product__compare-price">
                                    {format(prices.listPrice, 'USD').replace('.00', '')}
                                </span>
                                </>
                            )}
                            <span class="sr-only visually-hidden">Final Price:</span>
                            <span class="ns-product__price">
                                {/* {format(prices.price, 'USD').replace('.00', '')} */}
                                ${prices.price}
                            </span>
                            </>
                        )}
                    </div>
                    <div
                        class="ns-oke-reviews ns-flex ns-flex-nowrap ns-items-center"
                        data-oke-reviews-product-id={`shopify-${product.productId}`}
                        data-oke-star-rating=""
                        ref={reviewsRef}
                    />
                </a>

                {(skuByColor && colors?.length > 1) && (
                    <fieldset class="ns-product__swatches-fieldset ns-w-full" aria-label="Select a color">
                        <legend className="sr-only visually-hidden">
                            Color: {skuByColor[currentColor]?.customFields?.find(field => field?.key === 'color')?.value}
                        </legend>
                        <div class="ns-product__swatches-wrap ns-w-full ns-flex ns-flex-wrap ns-items-center">
                            {colors?.slice(0, swatchesDisplayed).map(color => {
                                const colorData = skuByColor[color];
                                const colorName = colorData.customFields?.find(field => field.key === 'color')?.value;
                                const swatchImage = colorData.customFields?.find(field => field.key === 'cql-swatch_image')?.value?.replace('--/', '--/w_50,h_50/');
                                return (
                                    <>
                                        <input
                                            id={`ns-swatch-${product.productId}-${colorData.id}`}
                                            className="ns-product__swatch-input sr-only visually-hidden"
                                            name={`ns-swatch-${product.productId}`}
                                            type="radio"
                                            checked={skuByColor[currentColor]?.id === colorData.id}
                                            value={colorData.id}
                                            onChange={() => handleSkuChange(color)}
                                            aria-checked={skuByColor[currentColor]?.id === colorData.id}
                                        />
                                        <label className="ns-product__swatch ns-relative ns-flex ns-item-center ns-justify-center" htmlFor={`ns-swatch-${product.productId}-${colorData.id}`}>
                                            <span className="sr-only visually-hidden">
                                                {colorName.concat(colorData.availability !== 'InStock' ? ' - out of stock' : '')}
                                            </span>
                                            <img
                                                src={swatchImage}
                                                className="ns-product__swatch-img"
                                                aria-hidden={true}
                                                alt=""
                                                width="25"
                                                height="25"
                                            />
                                        </label>
                                    </>
                                )
                            })}
                            {(colors?.length > 0 && swatchesDisplayed < colors?.length) && (
                                <button
                                    className="ns-product__expand-swatches"
                                    type="button"
                                    onClick={showAllSwatches}
                                >
                                    +{(colors?.length - swatchesDisplayed)} 
                                    <span> more</span>
                                </button>
                            )}
                        </div>
                    </fieldset>
                )}
            </div>
        </SerpElement>
    )
}